/**
 * Generic hook for enriching entities with draft data
 *
 * This hook eliminates duplicate code across features by providing a reusable
 * pattern for combining entity arrays with their draft information.
 *
 * @example
 * // Map-based return (default)
 * const draftsMap = useEntitiesWithDrafts({
 *   entities: employees,
 *   useDraftHook: useEmployeeDraft,
 * });
 *
 * @example
 * // Array-based return with enriched entities
 * const enrichedEntities = useEntitiesWithDrafts({
 *   entities: voucherCampaigns,
 *   useDraftHook: useVoucherCampaignDraft,
 *   returnType: "enriched",
 * });
 */
import { useMemo } from "react";
import type { Draft } from "@/shared/contexts/DraftContext/DraftContext";

/**
 * Minimal interface for draft hooks - requires getDraft and getChangedFields
 * Optional hasDraft for enriched return type
 */
export type DraftHookResult<T = unknown> = {
  getDraft: (id: string | null) => Draft<T> | null
  getChangedFields: (id: string | null) => string[]
  hasDraft?: (id: string | null) => boolean
};

/**
 * Entity with an id property
 */
export type EntityWithId = { id: string };

/**
 * Draft info stored in the map
 */
export type DraftInfo<T = unknown> = {
  draft: Draft<T> | null
  changedFields: string[]
};

/**
 * Enriched entity with draft information attached
 */
export type EnrichedEntity<T extends EntityWithId, D = unknown> = T & {
  draft: Draft<D> | null
  hasDraft: boolean
  changedFields: string[]
};

/**
 * Options for the useEntitiesWithDrafts hook
 */
export type UseEntitiesWithDraftsOptions<
  T extends EntityWithId,
  D = unknown,
> = {
  /** Array of entities to enrich with draft data */
  entities: T[]
  /** Draft hook that provides getDraft and getChangedFields */
  useDraftHook: () => DraftHookResult<D>
  /** Return type: "map" for Map<id, DraftInfo>, "enriched" for array with draft properties */
  returnType?: "map" | "enriched"
};

/**
 * Return type based on options
 */
export type UseEntitiesWithDraftsResult<
  T extends EntityWithId,
  D = unknown,
  R extends "map" | "enriched" = "map",
> = R extends "enriched" ? EnrichedEntity<T, D>[] : Map<string, DraftInfo<D>>;

/**
 * Hook for enriching entities with draft data
 *
 * @param options Configuration options
 * @returns Map of entity ID to draft data, or enriched entity array
 */
export function useEntitiesWithDrafts<
  T extends EntityWithId,
  D = unknown,
>(
  options: UseEntitiesWithDraftsOptions<T, D> & { returnType: "enriched" },
): EnrichedEntity<T, D>[];

export function useEntitiesWithDrafts<
  T extends EntityWithId,
  D = unknown,
>(
  options: UseEntitiesWithDraftsOptions<T, D> & { returnType?: "map" },
): Map<string, DraftInfo<D>>;

export function useEntitiesWithDrafts<
  T extends EntityWithId,
  D = unknown,
>(
  options: UseEntitiesWithDraftsOptions<T, D>,
): Map<string, DraftInfo<D>> | EnrichedEntity<T, D>[] {
  const { entities, useDraftHook, returnType = "map" } = options;
  const { getDraft, getChangedFields, hasDraft } = useDraftHook();

  return useMemo(() => {
    if (returnType === "enriched") {
      return entities.map((entity) => ({
        ...entity,
        draft: getDraft(entity.id),
        hasDraft: hasDraft?.(entity.id) ?? getDraft(entity.id) !== null,
        changedFields: getChangedFields(entity.id),
      }));
    }

    // Default: return map with only entities that have drafts or changed fields
    const draftsMap = new Map<string, DraftInfo<D>>();
    entities.forEach((entity) => {
      const draft = getDraft(entity.id);
      const changedFields = getChangedFields(entity.id);
      if (draft || changedFields.length > 0) {
        draftsMap.set(entity.id, { draft, changedFields });
      }
    });
    return draftsMap;
  }, [entities, getDraft, getChangedFields, hasDraft, returnType]);
}
